/**
 * InvoiceItemsTable Component
 * Fatura kalemlerini gösteren tablo component'i
 *
 * Sütun yapısı orijinaldir. Yalnızca giriş alanları yenilendi:
 * - Alanlar okunaklı ölçüde (MUI 'small', ~40px, 0.85rem) — eski 2px dolgulu 0.75rem yerine.
 * - Sayısal alanlar boş bırakılabilir; otomatik "0" yazılmaz. Boş alan hesaplamada sıfır
 *   sayılır ve kaydetme sırasında uyarı olarak listelenir (bkz. utils/invoiceItemFields).
 */

import React from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { InvoiceItem } from '../types/invoice.types';
import { formatQuantityForDisplay, getCurrencySymbol } from '../../utils/invoiceCalculations';
import { displayTextFor, isFieldEmpty, type EditableNumberField } from '../../utils/invoiceItemFields';
import { formatAmount } from '../../../../../utils/numberFormat';
import { InvoiceSection, SectionTitle } from '../styled/InvoiceStyledComponents';
import { InvoiceNumberField } from '../shared/InvoiceNumberField';

interface InvoiceItemsTableProps {
  selectedItems: InvoiceItem[];
  currency: string;
  exchangeRate: string;
  loading: boolean;
  unitOptions: string[];
  onOpenItemModal: () => void;
  onAddManualItem: () => void;
  onOpenBulkWarehouseModal: () => void;
  onItemNameChange: (id: string, value: string) => void;
  onDisplayQuantityChange: (id: string, value: string) => void;
  onQuantityCommitOnBlur: (id: string) => void;
  onUnitChange: (id: string, value: string) => void;
  onPriceTypeChange: (id: string, value: 'VAT_EXCLUDED' | 'VAT_INCLUDED') => void;
  /** Kullanıcı yazarken yalnızca metni günceller (hesaplama yapılmaz). */
  onItemDisplayChange: (id: string, field: EditableNumberField, raw: string) => void;
  /** Odak çıkışı / Enter — metni sayıya çevirip kalemi yeniden hesaplar. */
  onItemNumberCommit: (id: string, field: EditableNumberField) => void;
  /** İndirim giriş modu (kolon-seviyesi): tüm kalemler için ya yüzde ya tutar */
  discountMode: 'PERCENT' | 'AMOUNT';
  /** Kolon başlığındaki seçiciden mod değiştiğinde — tüm kalemlere uygulanır */
  onDiscountModeChange: (mode: 'PERCENT' | 'AMOUNT') => void;
  onDeleteItem: (id: string) => void;
  /** Mevcut belge tipi — 'Başka {Belge}den' butonunun etiketini belirler */
  mode?: 'invoice' | 'offer' | 'order' | 'waybill';
  /** Başka belgeden kalem aktarma modalını açan handler. Tanımlı değilse buton gizlenir. */
  onOpenCopyFromDocumentModal?: () => void;
  /** Kaydetme denendi mi — boş kalan zorunlu alanları kırmızı göstermek için. */
  attemptedSave?: boolean;
}

const headCell = { py: 0.5, px: 1, fontSize: '0.75rem', whiteSpace: 'nowrap' as const };
const bodyCell = { py: 0.75, px: 1, verticalAlign: 'middle' as const };
const readOnlyText = { fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const };

export const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  selectedItems,
  currency,
  exchangeRate,
  loading,
  unitOptions,
  onOpenItemModal,
  onAddManualItem,
  onOpenBulkWarehouseModal,
  onItemNameChange,
  onDisplayQuantityChange,
  onQuantityCommitOnBlur,
  onUnitChange,
  onPriceTypeChange,
  onItemDisplayChange,
  onItemNumberCommit,
  discountMode,
  onDiscountModeChange,
  onDeleteItem,
  mode,
  onOpenCopyFromDocumentModal,
  attemptedSave,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const docTrLabel: Record<NonNullable<InvoiceItemsTableProps['mode']>, string> = {
    invoice: 'Faturadan',
    offer: 'Teklifden',
    order: 'Siparişten',
    waybill: 'İrsaliyeden',
  };
  const docEnLabel: Record<NonNullable<InvoiceItemsTableProps['mode']>, string> = {
    invoice: 'Invoice',
    offer: 'Offer',
    order: 'Order',
    waybill: 'Waybill',
  };
  const copyButtonLabel = (() => {
    const m = mode || 'invoice';
    return tt(`Başka ${docTrLabel[m]}`, `From Another ${docEnLabel[m]}`);
  })();
  // Bolum basligi belge turune gore: Fatura/Teklif/Siparis/Irsaliye Kalemleri
  const docNounTr: Record<NonNullable<InvoiceItemsTableProps['mode']>, string> = {
    invoice: 'Fatura',
    offer: 'Teklif',
    order: 'Sipariş',
    waybill: 'İrsaliye',
  };
  const itemsTitle = (() => {
    const m = mode || 'invoice';
    return tt(`${docNounTr[m]} Kalemleri`, `${docEnLabel[m]} Items`);
  })();
  const unitLabelMap: Record<string, string> = {
    adet: 'piece',
    kg: 'kg',
    gr: 'g',
    lt: 'l',
    ml: 'ml',
    m: 'm',
    cm: 'cm',
    'm²': 'm²',
    'm³': 'm³',
    çift: 'pair',
    paket: 'pack',
    kutu: 'box',
    top: 'roll',
    rulo: 'roll',
  };

  const localizeUnit = (unit: string) => (isTr ? unit : (unitLabelMap[unit] || unit));

  const normalizeUnitInput = (input: string) => {
    if (isTr) return input;
    const normalized = input.trim().toLowerCase();
    const matched = Object.entries(unitLabelMap).find(([, enLabel]) => enLabel.toLowerCase() === normalized);
    return matched?.[0] || input;
  };

  const symbol = getCurrencySymbol(currency);

  return (
    <InvoiceSection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
          {itemsTitle}
        </SectionTitle>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={onOpenItemModal}
            disabled={loading}
            size="small"
            sx={{ mr: 1 }}
          >
            {tt('Ürün Ekle', 'Add Item')}
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<NotListedLocationIcon />}
            onClick={onAddManualItem}
            disabled={loading}
            size="small"
            sx={{ mr: 1 }}
          >
            {tt('Stoksuz Ürün Ekle', 'Add Non-Stock Item')}
          </Button>
          {onOpenCopyFromDocumentModal && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<ContentCopyIcon />}
              onClick={onOpenCopyFromDocumentModal}
              disabled={loading}
              size="small"
              sx={{ mr: 1 }}
            >
              {copyButtonLabel}
            </Button>
          )}
          <Button 
            variant="outlined" 
            color="info" 
            startIcon={<WarehouseIcon />}
            onClick={onOpenBulkWarehouseModal}
            disabled={loading || selectedItems.length === 0}
            size="small"
          >
            {tt('Toplu Depo Seç', 'Bulk Warehouse Select')}
          </Button>
        </Box>
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...headCell, fontWeight: 'bold', width: 40 }}>{tt('Sıra', 'No')}</TableCell>
              <TableCell sx={{ ...headCell, minWidth: 200 }}>{tt('Ürün', 'Item')}</TableCell>
              <TableCell align="right" sx={headCell}>{tt('Miktar', 'Quantity')}</TableCell>
              <TableCell align="right" sx={headCell}>{tt('Birim', 'Unit')}</TableCell>
              <TableCell align="right" sx={{ ...headCell, bgcolor: 'warning.lighter', fontWeight: 'bold' }}>{tt('Fiyatı', 'Price')}</TableCell>
              <TableCell align="center" sx={{ ...headCell, fontWeight: 'bold' }}>KDV</TableCell>
              <TableCell align="right" sx={headCell}>KDV %</TableCell>
              <TableCell align="right" sx={headCell}>{tt('KDV Hariç', 'Excl. VAT')}</TableCell>
              <TableCell align="right" sx={{ ...headCell, bgcolor: 'success.lighter', fontWeight: 'bold' }}>{tt('KDV Dahil', 'Incl. VAT')}</TableCell>
              <TableCell align="right" sx={{ ...headCell, px: 0.5 }}>
                <FormControl size="small" variant="standard" sx={{ minWidth: 92 }}>
                  <Select
                    value={discountMode}
                    onChange={(e) => onDiscountModeChange(e.target.value as 'PERCENT' | 'AMOUNT')}
                    disableUnderline
                    sx={{ fontSize: '0.75rem', fontWeight: 'bold', '& .MuiSelect-select': { py: 0, pr: '20px !important' } }}
                  >
                    <MenuItem value="PERCENT" sx={{ fontSize: '0.75rem' }}>{tt('İndirim %', 'Discount %')}</MenuItem>
                    <MenuItem value="AMOUNT" sx={{ fontSize: '0.75rem' }}>{tt(`İndirim ${symbol}`, `Discount ${symbol}`)}</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="right" sx={{ ...headCell, bgcolor: 'info.lighter', fontWeight: 'bold' }}>{tt('Tutar (KDV Dahil)', 'Amount (Incl. VAT)')}</TableCell>
              <TableCell align="right" sx={headCell}>{tt('KDV Tutarı', 'VAT Amount')}</TableCell>
              {currency !== 'TRY' && (
                <TableCell align="right" sx={headCell}>TRY</TableCell>
              )}
              <TableCell align="right" sx={headCell}>{tt('İşlem', 'Action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={currency !== 'TRY' ? 15 : 14} align="center" sx={{ fontSize: '0.85rem', py: 2 }}>
                  {tt('Henüz ürün eklenmedi. Lütfen "Ürün Ekle" butonuna tıklayın.', 'No items added yet. Please click "Add Item".')}
                </TableCell>
              </TableRow>
            ) : (
              selectedItems.map((item, index) => {
                const isManual = item.itemId.startsWith('manual_');
                return (
                <TableRow key={item.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  <TableCell align="center" sx={{ ...bodyCell, fontSize: '0.8rem', fontWeight: 'bold', color: 'primary.main' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={bodyCell}>
                    {isManual ? (
                      <TextField
                        size="small"
                        value={item.itemName}
                        onChange={(e) => onItemNameChange(item.id, e.target.value)}
                        placeholder={tt('Ürün adı girin', 'Enter item name')}
                        sx={{ width: '100%', minWidth: 180, '& input': { fontSize: '0.85rem' } }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem' }}>{item.itemName}</Typography>
                    )}
                  </TableCell>

                  {/* Miktar */}
                  <TableCell align="right" sx={bodyCell}>
                    <InvoiceNumberField
                      text={item.displayQuantity ?? formatQuantityForDisplay(item.quantity)}
                      onTextChange={(raw) => onDisplayQuantityChange(item.id, raw)}
                      onCommit={() => onQuantityCommitOnBlur(item.id)}
                      invalid={attemptedSave === true && isFieldEmpty(item, 'quantity')}
                      width={90}
                    />
                  </TableCell>

                  {/* Birim */}
                  <TableCell align="right" sx={bodyCell}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={unitOptions}
                      getOptionLabel={(option) => localizeUnit(option)}
                      renderOption={(props, option) => (
                        <li {...props}>{localizeUnit(option)}</li>
                      )}
                      value={item.unit}
                      onChange={(_e, newValue) => onUnitChange(item.id, normalizeUnitInput(newValue || ''))}
                      onInputChange={(_e, newValue) => onUnitChange(item.id, normalizeUnitInput(newValue || ''))}
                      disabled={!isManual}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={tt('Birim', 'Unit')}
                          sx={{
                            bgcolor: isManual ? 'background.paper' : 'action.disabledBackground',
                            '& input': { fontSize: '0.85rem' },
                          }}
                        />
                      )}
                      sx={{ width: 110 }}
                      disableClearable
                    />
                  </TableCell>
                  
                  {/* Fiyatı (enteredPrice) */}
                  <TableCell align="right" sx={bodyCell}>
                    <InvoiceNumberField
                      text={displayTextFor(item, 'price')}
                      onTextChange={(raw) => onItemDisplayChange(item.id, 'price', raw)}
                      onCommit={() => onItemNumberCommit(item.id, 'price')}
                      suffix={symbol}
                      invalid={attemptedSave === true && isFieldEmpty(item, 'price')}
                      width={125}
                      sx={{ bgcolor: 'warning.lighter' }}
                    />
                  </TableCell>
                  
                  {/* KDV Dropdown (priceType) */}
                  <TableCell align="center" sx={bodyCell}>
                    <FormControl size="small" sx={{ width: 120 }}>
                      <Select
                        value={item.priceType}
                        onChange={(e) => onPriceTypeChange(item.id, e.target.value as 'VAT_EXCLUDED' | 'VAT_INCLUDED')}
                        size="small"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        <MenuItem value="VAT_EXCLUDED" sx={{ fontSize: '0.85rem' }}>{tt('Hariç', 'Excluded')}</MenuItem>
                        <MenuItem value="VAT_INCLUDED" sx={{ fontSize: '0.85rem' }}>{tt('Dahil', 'Included')}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  
                  {/* KDV % */}
                  <TableCell align="right" sx={bodyCell}>
                    <InvoiceNumberField
                      text={displayTextFor(item, 'vatRate')}
                      onTextChange={(raw) => onItemDisplayChange(item.id, 'vatRate', raw)}
                      onCommit={() => onItemNumberCommit(item.id, 'vatRate')}
                      suffix="%"
                      invalid={attemptedSave === true && isFieldEmpty(item, 'vatRate')}
                      width={85}
                    />
                  </TableCell>
                  
                  {/* KDV Hariç */}
                  <TableCell align="right" sx={bodyCell}>
                    <Typography sx={{ ...readOnlyText, color: 'text.secondary' }}>
                      {formatAmount(item.unitPrice)} {symbol}
                    </Typography>
                  </TableCell>
                  
                  {/* KDV Dahil */}
                  <TableCell align="right" sx={{ ...bodyCell, bgcolor: 'success.lighter' }}>
                    <Typography sx={{ ...readOnlyText, fontWeight: 'bold', color: 'success.dark' }}>
                      {formatAmount(item.unitPriceWithVat)} {symbol}
                    </Typography>
                  </TableCell>
                  
                  {/* İndirim (kolon moduna göre % veya ₺) */}
                  <TableCell align="right" sx={bodyCell}>
                    {discountMode === 'AMOUNT' ? (
                      <InvoiceNumberField
                        text={displayTextFor(item, 'discountAmount')}
                        onTextChange={(raw) => onItemDisplayChange(item.id, 'discountAmount', raw)}
                        onCommit={() => onItemNumberCommit(item.id, 'discountAmount')}
                        suffix={symbol}
                        width={110}
                      />
                    ) : (
                      <InvoiceNumberField
                        text={displayTextFor(item, 'discountRate')}
                        onTextChange={(raw) => onItemDisplayChange(item.id, 'discountRate', raw)}
                        onCommit={() => onItemNumberCommit(item.id, 'discountRate')}
                        suffix="%"
                        width={85}
                      />
                    )}
                  </TableCell>
                  
                  {/* Tutar (KDV Dahil) */}
                  <TableCell align="right" sx={{ ...bodyCell, bgcolor: 'info.lighter' }}>
                    <Typography sx={{ ...readOnlyText, fontWeight: 'bold', color: 'info.dark' }}>
                      {formatAmount(item.grossTotal)} {symbol}
                    </Typography>
                  </TableCell>
                  
                  {/* KDV Tutarı */}
                  <TableCell align="right" sx={{ ...bodyCell, ...readOnlyText, color: 'success.main', fontWeight: 'medium' }}>
                    {formatAmount(item.vatAmount)} {symbol}
                  </TableCell>
                  
                  {currency !== 'TRY' && (
                    <TableCell align="right" sx={{ ...bodyCell, ...readOnlyText }}>
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(item.grossTotal * (parseFloat(exchangeRate) || 0))}
                    </TableCell>
                  )}
                  
                  <TableCell align="right" sx={bodyCell}>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => onDeleteItem(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </InvoiceSection>
  );
};
